/**
 * What src/meta keeps beyond the contract in `types.ts`. The run's extra bookkeeping rides
 * along as additional JSON fields: every `OperationState` is a valid `Operation`, every
 * `ProfileState` a valid `Profile`. Every field added here is defaulted on load (save.ts).
 */

import type { ArchetypeId, IntelId } from '../sim/types';
import type { Deployment } from './api';
import type {
  DailyEntry, MissionSummary, Operation, Profile, RangerRecord, SpecialItem, TheatreNode,
} from './types';

/** The mission in the air: set by `deploy`, cleared by `report`. There is no un-deploy (04 §3). */
export interface PendingDeployment {
  nodeId: string;
  rangerId: string;
  cost: number;
  deployment: Deployment;
}

export interface OperationState extends Operation {
  deployed: PendingDeployment | null;
  /** The open POW Rescue node, while captives wait (04 §2.2). */
  rescueNodeId: string | null;
  /** Rare equipment found in supply caches; `deploy` takes `loadout.gear` out of it (04 §6). */
  stock: Partial<Record<SpecialItem, number>>;
  /** Free intel cards (Signals Intercept), spent on the next deployment. */
  intelHeld: IntelId[];
  /** Theatres whose modifiers an intercept has revealed. */
  revealedTheatres: number[];
  /** Nodes that have had their readiness-6 forced-modifier roll. */
  forcedRolled: string[];
  /** Rangers whose Lucky charge is spent for this operation. */
  luckyUsed: string[];
  /** Cadet option: theatres whose one restart is used. */
  cadetRestarts: number[];
  /** Hall of Honor inductions made from this operation. */
  inducted: number;
  /** Tag Recovery (04 §2.3): marked node id -> id of the fallen ranger whose tags lie there. */
  tagNodes: Record<string, string>;
}

/** What the profile remembers about one archetype beyond `bestScores` (04 §5 Records). */
export interface ArchetypeStats {
  attempts: number;
  completions: number;
  /** Fastest success, seconds of mission clock; null until a success reports its time. */
  bestSeconds: number | null;
}

export interface ProfileState extends Profile {
  operation: OperationState | null;
  nextRangerId: number;
  /** `archetype:biome` pairs completed at least once (a commendation each, 04 §5). */
  firsts: string[];
  /** Precise landings per ranger id, toward the Pathfinder Badge. */
  landings: Record<string, number>;
  /** The Daily Drop's local table, newest first, at most `DAILY.keep` entries (04 §11). */
  daily: DailyEntry[];
  /** Date of the Daily Drop in the air: the one entry `report` may still overwrite. */
  dailyPending: string | null;
  /** Operation and Classic missions flown per archetype (practice and the daily do not count). */
  archetypeStats: Partial<Record<ArchetypeId, ArchetypeStats>>;
  /** Successes in a row per living ranger id, and the longest such run anyone has had. */
  streaks: Record<string, number>;
  bestStreak: number;
}

/**
 * Facts the debrief would like from the sim that `MissionSummary` does not carry yet. All
 * optional: when absent the layer falls back to the conservative reading.
 */
export interface SummaryExtras {
  luckyUsed?: boolean;
  captivesTotal?: number;
  allPartials?: boolean;
  preciseLanding?: boolean;
  downedByExplosion?: boolean;
  /** He took a wound at some point, whether or not it was still open at the pickup (Purple Heart). */
  woundedInAction?: boolean;
  cause?: string;
}

export type FullSummary = MissionSummary & SummaryExtras;

export const SAVE_VERSION = 1;

/** A day-one profile. */
export function freshProfile(): ProfileState {
  return {
    version: SAVE_VERSION, commendations: 0, unlocks: [], hall: [], retired: [], memorial: [], operation: null,
    classicRoster: [], bestScores: {}, operationsWon: 0, hazardUnlocked: 0, nextRangerId: 1, firsts: [], landings: {},
    daily: [], dailyPending: null, archetypeStats: {}, streaks: {}, bestStreak: 0, flights: {},
  };
}

/** Deep copy of plain JSON data: nothing handed across the API is shared with the live profile. */
export function snapshot<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/** The running operation, or an Error the menus can show. */
export function activeOperation(profile: ProfileState): OperationState {
  const op = profile.operation;
  if (!op || op.status !== 'active') throw new Error('There is no operation under way.');
  return op;
}

export function findNode(op: Operation, nodeId: string): TheatreNode {
  const node = op.nodes.find((n) => n.id === nodeId);
  if (!node) throw new Error(`No such node: ${nodeId}.`);
  return node;
}

export function companyRanger(op: Operation, rangerId: string): RangerRecord {
  const ranger = op.company.find((r) => r.id === rangerId);
  if (!ranger) throw new Error('That ranger is not in the company.');
  return ranger;
}

/** Any living record the player can still act on: company, Hall of Honor, Classic roster. */
export function findRanger(profile: ProfileState, rangerId: string): RangerRecord | null {
  const pools = [profile.operation?.company ?? [], profile.hall, profile.classicRoster];
  for (const pool of pools) {
    const hit = pool.find((r) => r.id === rangerId);
    if (hit) return hit;
  }
  return null;
}

export function fullName(r: RangerRecord): string {
  return `${r.firstName} ${r.surname}`;
}

export function newRangerId(profile: ProfileState): string {
  const id = `R${profile.nextRangerId}`;
  profile.nextRangerId += 1;
  return id;
}

/** Still with the company in body: may fly again one day. */
export function isAlive(r: RangerRecord): boolean {
  return r.status === 'ready' || r.status === 'wounded' || r.status === 'critical' || r.status === 'mia';
}
